import {
    Notification,
    useGetNotificationsMutation,
    useMarkAllNotificationsAsSeenMutation,
    useMarkNotificationAsSeenMutation
} from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useNotificationsPageVM = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [getNotifications, { isLoading, isSuccess, data, isError }] = useGetNotificationsMutation();
    const [markNotificationAsSeen] = useMarkNotificationAsSeenMutation();
    const [markAllNotificationsAsSeen, { isLoading: isMarkingAll }] = useMarkAllNotificationsAsSeenMutation();
    const navigate = useNavigate();

    useEffect(() => {
        getNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isSuccess && data) {
            setNotifications(data);
        }
    }, [isSuccess, data]);

    const hasUnseen = useMemo(() => notifications.some((n) => !n.seen), [notifications]);

    const markOneLocally = (id: number) => {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, seen: true } : n)));
    };

    const markAllLocally = () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })));
    };

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.seen) {
            markOneLocally(notification.id);
            // Fire-and-forget: the backend call runs in parallel with the
            // navigation so the user doesn't wait on the network.
            markNotificationAsSeen(notification.id);
        }

        if (notification.entityId && notification.entityType) {
            switch (notification.entityType) {
                case 'news':
                    navigate('/news');
                    break;
                case 'document':
                    navigate('/richesses-humaines/bibliotheque');
                    break;
                case 'causerie':
                    if (notification.entityParentId) {
                        navigate(`/sante-securite/causeries/list/${notification.entityParentId}/emargement/${notification.entityId}`);
                    } else {
                        navigate('/sante-securite/causeries');
                    }
                    break;
                case 'formation':
                    if (notification.entityParentId) {
                        navigate(`/sante-securite/formations/list/${notification.entityParentId}/emargement/${notification.entityId}`);
                    } else {
                        navigate('/sante-securite/formations');
                    }
                    break;
                case 'conge':
                    navigate(`/richesses-humaines/conges/${notification.entityId}`);
                    break;
                case 'pointage':
                    navigate(`/richesses-humaines/pointage/${notification.entityId}`);
                    break;
                case 'quitus':
                    navigate(`/mes-chantiers/quitus/${notification.entityId}`);
                    break;
                case 'testsheet':
                    navigate(`/mes-chantiers/testsheets/${notification.entityId}`);
                    break;
                case 'event-feedback':
                    navigate('/event-feedback');
                    break;
                case 'bibliotheque-rh':
                    navigate('/richesses-humaines/bibliotheque');
                    break;
                case 'bibliotheque-sst':
                    navigate('/sante-securite/bibliotheque');
                    break;
                case 'bibliotheque-chantier':
                    navigate('/mes-chantiers/bibliotheque');
                    break;
                default:
                    break;
            }
        }
    };

    const handleMarkAllAsSeen = () => {
        if (!hasUnseen || isMarkingAll) return;
        markAllLocally();
        markAllNotificationsAsSeen();
    };

    return {
        notifications,
        isLoading,
        isError,
        hasUnseen,
        isMarkingAll,
        handleNotificationClick,
        handleMarkAllAsSeen
    };
};
